import React, { useContext } from 'react';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeStyles, Theme } from '@material-ui/core/styles';
import ColumnChart from 'components/@common/Charts/ColumnChart';
import Popover from 'components/@common/Popover';
import Skeleton from 'components/@common/Skeleton';
import { PrintViewContext } from '../providers/PrintViewProvider';

const useStyles = makeStyles((theme: Theme) => {
  return {
    card: {
      boxSizing: 'border-box',
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingBottom: theme.spacing(2),
      paddingLeft: theme.spacing(3)
    },
    chartContainer: { width: '100%' },
    skeleton: {
      marginRight: theme.spacing(3),
      marginLeft: theme.spacing(3)
    },
    textContainer: { padding: theme.spacing(1) },
    inline: { display: 'inline' }
  };
});

interface BarAndLineChartCardProps {
  title: string;
  helpText?: string;
  series: ApexAxisChartSeries | ApexNonAxisChartSeries | undefined;
  categories: string[];
  options?: any;
  renderTextInformation: any;
  loading: boolean;
}

const BarAndLineChartCard: React.FC<BarAndLineChartCardProps> = ({
  title,
  helpText,
  series,
  categories,
  renderTextInformation,
  loading
}): React.ReactElement => {
  const classes = useStyles();
  const { fullChartWidth } = useContext(PrintViewContext);
  const titleComponent = <Typography variant="h3">{title}</Typography>;

  return (
    <Paper className={classes.card}>
      {helpText ? <Popover description={helpText}>{titleComponent}</Popover> : titleComponent}
      <Grid container direction="column" wrap="nowrap" className={classes.chartContainer}>
        <Grid item>
          {loading ? (
            <Skeleton height={230} className={classes.skeleton} />
          ) : (
            <ColumnChart
              height={230}
              width={fullChartWidth}
              type="line"
              series={series}
              options={{
                chart: {
                  fontFamily: 'futura-pt',
                  zoom: { enabled: false },
                  toolbar: { show: false }
                },
                markers: {
                  size: 4,
                  colors: '#F4A521',
                  strokeColors: '#F4A521',
                  strokeWidth: 2,
                  fillOpacity: 1,
                  shape: 'circle',
                  radius: 2
                },
                dataLabels: { enabled: false },
                legend: {
                  offsetY: 2,
                  horizontalAlign: 'left'
                },
                colors: ['#006DB8', '#F4A521'],
                stroke: {
                  width: [0, 4]
                },

                // labels: {
                //   style: { fontSize: '8px' }
                // },
                yaxis: [
                  {
                    title: {
                      offsetX: -2,
                      text: `No of ${(series?.[0] as any)?.name}`,
                      style: {
                        color: '#373D3F',
                        fontSize: '12px',
                        fontWeight: 700,
                        fontFamily: 'futura-pt'
                      }
                    }
                  },
                  {
                    opposite: true,
                    title: {
                      offsetX: 2,
                      text: (series?.[1] as any)?.name,
                      style: {
                        color: '#373D3F',
                        fontSize: '12px',
                        fontWeight: 700,
                        fontFamily: 'futura-pt'
                      }
                    }
                  }
                ],
                xaxis: {
                  categories,
                  labels: {
                    rotate: -45,
                    style: {
                      colors: '#373D3F',
                      fontSize: '10px',
                      fontFamily: 'futura-pt'
                    }
                  },
                  axisTicks: { show: false },
                  tooltip: {
                    enabled: false
                  }
                },
                tooltip: {
                  enabled: true,
                  intersect: false,
                  marker: { show: false },
                  theme: 'dark'
                }
              }}
            />
          )}
        </Grid>
        <Grid item className={classes.textContainer}>
          {loading ? <Skeleton height={48} /> : renderTextInformation()}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default BarAndLineChartCard;
